import React, { FC, useState } from 'react';
import { DevicesBox, ErrorBox, GoBackButton } from '../../styles';
import { useDeviceHandler } from '~Components/DeviceProvider';
import SelectableDevice from './SelectableDevice';
import styled from '@emotion/styled';
import { BlueButton, Box } from '~Styles';
import { useButton, useI18n, useLogger, useMetadata } from '~Hooks';
import Loader from '~ComponentsImproved/Loader';
import Paragraph from '~ComponentsImproved/Typography/Paragraph';
import { useStorage } from '~Components/Storage';

const Wrapper = styled(Box)`
  flex: 1;
  padding-top: 4px;
  overflow: hidden;
  background-color: var(--colorBlueGray1200);
  color: var(--colorWhite);
`;

interface Props {
  onComplete: () => void;
  /** Null if we don't have any devices - only option is to create a new */
  goBack: (() => void) | null;
}

/** Claim an existing device */
const Claim: FC<Props> = ({ onComplete, goBack }) => {
  const { i18n } = useI18n();

  const metadata = useMetadata();
  const logger = useLogger('claim-device');
  const [, updateStorage] = useStorage();

  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState<boolean>(false);
  const [selectedDeviceId, setSelectedDeviceId] = useState<string | null>(null);

  const deviceHandler = useDeviceHandler();

  const isUsingBrowser = !window.ReactNativeWebView;

  const handleClaimDevice = async () => {
    setLoading(true);

    logger.info('Claiming another device', { selectedDeviceId });

    if (!selectedDeviceId) {
      setLoading(false);
      return;
    }

    const deviceToUse = deviceHandler.allDevices.find((device) => device.id === selectedDeviceId);

    if (!deviceToUse) {
      setLoading(false);
      setError('The selected device was not found');
      return;
    }

    try {
      /**
       * If we're logged in browser we can assume it's a Baemingo employee,
       * then we should not claim the device (since it causes a cooldown on the device) but instead
       * just set the same clientId as the device.ownerClientId to simulate usage.
       * This will result in that we use this device (see DeviceProvider)
       */
      if (isUsingBrowser) {
        updateStorage({
          clientId: deviceToUse.ownerClientId,
          deviceId: deviceToUse.id,
        });
      } else {
        const claimDeviceRes = await deviceHandler.claim(selectedDeviceId);

        if (!claimDeviceRes.success) {
          throw new Error('Failed to claim device');
        }
      }

      updateStorage({
        taxComplianceDeviceId: deviceToUse?.posDeviceId,
        taxComplianceDeviceName: deviceToUse?.name,
        taxComplianceCorporateId: metadata.data.organizationId,
      });

      onComplete();
    } catch (error) {
      logger.error(
        '(Failed to Claim Device) - ' +
          (typeof error === 'string' ? error : JSON.stringify(error)),
      );
      setError(typeof error === 'string' ? error : i18n('General.Error'));
    }

    setLoading(false);
  };

  const goBackButton = useButton({
    onClick: () => {
      if (goBack) {
        goBack();
      }
    },
    disabled: loading,
  });

  const submitButton = useButton({
    onClick: () => handleClaimDevice(),
    disabled: !selectedDeviceId || loading,
  });

  return (
    <Wrapper display="flex" flexDirection="column" alignItems="center" fluid>
      <DevicesBox fluid display="flex" flexDirection="column">
        {deviceHandler.allDevices.map((device) => (
          <SelectableDevice
            key={device.id}
            device={device}
            isSelected={selectedDeviceId === device.id}
            onSelect={() => setSelectedDeviceId(device.id)}
          />
        ))}
      </DevicesBox>

      <Box
        display="flex"
        flexDirection="row"
        gap="xs"
        marginTop="medium"
        alignItems="center"
        fluid>
        {goBack && (
          <GoBackButton fluid {...goBackButton}>
            {i18n('General.GoBack')}
          </GoBackButton>
        )}
        <BlueButton fluid {...submitButton}>
          {loading ? (
            <Loader size="sm" color="var(--colorWhite)" />
          ) : (
            i18n('ClaimDevice.ClaimDevice')
          )}
        </BlueButton>
      </Box>

      <ErrorBox padding="small" display="flex" flexDirection="column" gap="xs">
        {error && (
          <Paragraph level={4} bottom={0} color="var(--colorUIRed)">
            {error}
          </Paragraph>
        )}
      </ErrorBox>
    </Wrapper>
  );
};

export default Claim;
